import { useCallback } from 'react'
import { notifySuccess } from 'app/services/notify.client'

/** Copies text to clipboard with fallback for older browsers, then shows a success notification. */
export const copyToClipboard = async (
  text: string,
  message = `Copied to clipboard`,
  notificationId = `copy-to-clipboard`,
) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
  } else {
    // Fallback for non-secure contexts (HTTP)
    const textarea = document.createElement(`textarea`)
    textarea.value = text
    textarea.style.position = `fixed`
    textarea.style.opacity = `0`
    document.body.appendChild(textarea)
    textarea.select()
    // eslint-disable-next-line deprecation/deprecation -- fallback for non-secure contexts
    document.execCommand(`copy`)
    document.body.removeChild(textarea)
  }
  notifySuccess(message, notificationId)
}

/**
 * Copies text the server has not returned yet. Browsers only allow a clipboard write straight
 * after a click, so the write starts inside the click and is handed the text when it arrives.
 * Call it before the first `await` in the click handler.
 */
export const copyWhenReady = async (text: Promise<string>, message = `Copied to clipboard`) => {
  if (typeof ClipboardItem === `undefined` || !navigator.clipboard?.write) {
    await copyToClipboard(await text, message)
    return
  }
  const blob = text.then((value) => new Blob([value], { type: `text/plain` }))
  await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })])
  notifySuccess(message, `copy-to-clipboard`)
}

/** Hook that returns a stable copy-to-clipboard callback. */
export const useCopyToClipboard = () =>
  useCallback((...args: Parameters<typeof copyToClipboard>) => copyToClipboard(...args), [])
